import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useCallback } from 'react';
import { useMemo } from 'react';
import React from 'react';
import { useIntl } from 'react-intl';
import FHGTextField from '../../components/TextField';
import ModalDialog from '../../fhg/components/dialog/ModalDialog';
import FHGTypography from '../../fhg/components/Typography';
import useEditData from '../../fhg/components/edit/useEditData';
import { formatMessage } from '../../fhg/utils/Utils';

export const EXTRA_FIELD_TYPES = ['number', 'text', 'dropdown', 'yesno'];

/**
 * Component to edit a single operator extra field definition (label / type / required / options).
 *
 * @param extraFieldValue The extra field row to edit.
 * @param onSubmit Submit the changes to the row.
 * @param onClose Close the edit dialog.
 * @return {JSX.Element}
 * @constructor
 */
export default function ExtraFieldEdit({ extraFieldValue, onSubmit, onClose }) {
   const intl = useIntl();

   const [editValues, handleChange, { isChanged, getValue, setValue, defaultValues }] = useEditData(extraFieldValue);

   const fieldType = getValue('fieldType') || 'text';
   const options = getValue('options') || [];

   const handleCheckbox = useCallback((event) => {
      setValue(event.target.name, event.target.checked, true);
   }, [setValue]);

   const handleTypeChange = useCallback((event) => {
      const type = event.target.value;
      setValue('fieldType', type, true);
      if (type === 'dropdown' && options.length === 0) {
         setValue('options', [''], true);
      }
   }, [setValue, options]);

   const handleOptionChange = (index) => (event) => {
      const next = [...options];
      next[index] = event.target.value;
      setValue('options', next, true);
   };

   const handleOptionAdd = () => {
      setValue('options', [...options, ''], true);
   };

   const handleOptionDelete = (index) => () => {
      setValue('options', options.filter((option, i) => i !== index), true);
   };

   const optionError = useMemo(() => {
      if (fieldType !== 'dropdown') {
         return undefined;
      }
      const trimmed = options.map((option) => (option || '').trim()).filter((option) => option.length > 0);
      if (trimmed.length === 0) {
         return formatMessage(intl, 'extraField.options.required');
      }
      if (new Set(trimmed).size !== trimmed.length) {
         return formatMessage(intl, 'extraField.options.unique');
      }
      return undefined;
   }, [fieldType, options, intl]);

   /**
    * Submit the changes to the extra field row.
    */
   const handleSubmit = useCallback((event) => {
      if (event) {
         event.preventDefault();
         event.stopPropagation();
      }

      if (optionError) {
         return;
      }

      if (isChanged) {
         onSubmit && onSubmit({ ...defaultValues, ...editValues });
      }
   }, [editValues, defaultValues, isChanged, onSubmit, optionError]);

   return (
      <ModalDialog open={true} titleKey={'extraField.edit.title'} onSubmit={(event) => handleSubmit(event)}
         maxWidth={'sm'} fullWidth onClose={onClose} isEnabled={!optionError}>
         <FHGTextField
            name='label'
            type='text'
            autoFocus
            label={'Label'}
            style={{ marginBottom: 8 }}
            onChange={handleChange}
            value={getValue('label') || ''}
            inputProps={{ maxLength: 200 }}
            required
         />
         <TextField
            select
            name='fieldType'
            label={'Type'}
            value={fieldType}
            onChange={handleTypeChange}
            fullWidth
            size='small'
            margin='normal'
            required
         >
            {EXTRA_FIELD_TYPES.map((type) => (
               <MenuItem key={type} value={type}>
                  <FHGTypography id={`extraField.type.${type}`} />
               </MenuItem>
            ))}
         </TextField>
         <FormControlLabel
            control={<Checkbox name='isRequired' checked={!!getValue('isRequired')} onChange={handleCheckbox} />}
            label={<FHGTypography id={'extraField.isRequired.label'} />}
         />
         {fieldType === 'yesno' && (
            <FormControlLabel
               control={
                  <Checkbox name='allowExplanation' checked={!!getValue('allowExplanation')}
                     onChange={handleCheckbox} />
               }
               label={<FHGTypography id={'extraField.allowExplanation.label'} />}
            />
         )}
         {fieldType === 'dropdown' && (
            <Box mt={1}>
               <FHGTypography id={'extraField.options.label'} variant={'subtitle2'} />
               {options.map((option, index) => (
                  <Box key={index} display='flex' alignItems='center'>
                     <FHGTextField
                        name={`option${index}`}
                        type='text'
                        label={`Option ${index + 1}`}
                        onChange={handleOptionChange(index)}
                        value={option || ''}
                     />
                     <IconButton aria-label='delete option' onClick={handleOptionDelete(index)}
                        disabled={options.length <= 1}>
                        <DeleteIcon />
                     </IconButton>
                  </Box>
               ))}
               <Button color='primary' startIcon={<AddIcon />} onClick={handleOptionAdd}>
                  <FHGTypography noWrap id={'extraField.options.add'} />
               </Button>
               {optionError && (
                  <FHGTypography component={'div'} variant={'caption'} color={'error'}>{optionError}</FHGTypography>
               )}
            </Box>
         )}
      </ModalDialog>
   );
}
